/**
 * ZeoVerlaufPage - Archiv aller Zeo KI-Konversationen
 * Zeigt alle vergangenen Chat-Sessions mit Nachrichten und verwendeten Dateien
 */
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  MessageSquare, Search, ChevronRight, Clock, User, Bot,
  FileText, Trash2, ArrowLeft
} from 'lucide-react';
import { authAPI } from '@/api/authAPI';
import { toast } from 'sonner';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

export default function ZeoVerlaufPage() {
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => { loadSessions(); }, []);

  async function loadSessions() {
    setLoading(true);
    try {
      const token = authAPI.token;
      const res = await fetch(`${API_URL}/api/assistent/sessions?limit=100`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (res.ok) setSessions(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }

  async function loadSession(sessionId) {
    try {
      const token = authAPI.token;
      const res = await fetch(`${API_URL}/api/assistent/sessions/${sessionId}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (res.ok) {
        const data = await res.json();
        setSelectedSession(data);
        setMessages(data.messages || []);
      }
    } catch (e) { console.error(e); }
  }

  async function deleteSession(sessionId, e) {
    e.stopPropagation();
    if (!window.confirm('Session wirklich löschen?')) return;
    try {
      const token = authAPI.token;
      await fetch(`${API_URL}/api/assistent/sessions/${sessionId}`, {
        method: 'DELETE', headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      setSessions(prev => prev.filter(s => s.id !== sessionId));
      if (selectedSession?.id === sessionId) {
        setSelectedSession(null);
        setMessages([]);
      }
      toast.success('Session gelöscht');
    } catch (e) { toast.error('Fehler beim Löschen'); }
  }

  const filtered = sessions.filter(s =>
    (s.titel || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  function formatDate(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  }

  if (selectedSession) {
    return (
      <div className="p-6 max-w-4xl mx-auto" data-testid="zeo-verlauf-detail">
        <Button variant="ghost" onClick={() => { setSelectedSession(null); setMessages([]); }} className="mb-4" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" /> Zurück zur Übersicht
        </Button>
        <Card className="bg-slate-900 border-slate-700">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg text-white">{selectedSession.titel || 'Zeo Konversation'}</CardTitle>
            <div className="flex gap-3 text-xs text-slate-400 mt-1">
              <span><Clock className="w-3 h-3 inline mr-1" />{formatDate(selectedSession.created_date)}</span>
              {selectedSession.kontext_typ && (
                <Badge variant="outline" className="text-xs">{selectedSession.kontext_typ}: {selectedSession.kontext_id?.substring(0, 8)}...</Badge>
              )}
              <span>{messages.length} Nachrichten</span>
            </div>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[60vh]">
              <div className="space-y-3 pr-4">
                {messages.map((msg, i) => (
                  <div key={msg.id || i} className={`flex gap-3 ${msg.rolle === 'user' ? 'justify-end' : 'justify-start'}`}>
                    {msg.rolle !== 'user' && (
                      <div className="w-7 h-7 rounded-full bg-cyan-900/50 flex items-center justify-center shrink-0 mt-1">
                        <Bot className="w-4 h-4 text-cyan-400" />
                      </div>
                    )}
                    <div className={`max-w-[80%] rounded-xl px-4 py-2.5 text-sm ${
                      msg.rolle === 'user'
                        ? 'bg-blue-600 text-white'
                        : 'bg-slate-800 text-slate-200'
                    }`}>
                      <div className="whitespace-pre-wrap">{msg.text}</div>
                      <div className="text-[10px] opacity-50 mt-1">{formatDate(msg.timestamp)}</div>
                    </div>
                    {msg.rolle === 'user' && (
                      <div className="w-7 h-7 rounded-full bg-blue-900/50 flex items-center justify-center shrink-0 mt-1">
                        <User className="w-4 h-4 text-blue-400" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="zeo-verlauf-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Zeo Verlauf</h1>
          <p className="text-sm text-slate-400 mt-1">{sessions.length} Konversationen</p>
        </div>
      </div>

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <Input
          placeholder="Konversation suchen..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="pl-10 bg-slate-900 border-slate-700 text-white"
          data-testid="search-sessions"
        />
      </div>

      {loading ? (
        <p className="text-slate-400 text-center py-8">Lade Konversationen...</p>
      ) : filtered.length === 0 ? (
        <p className="text-slate-400 text-center py-8">Keine Konversationen gefunden</p>
      ) : (
        <div className="space-y-2">
          {filtered.map(s => (
            <Card
              key={s.id}
              className="bg-slate-900/60 border-slate-700/50 hover:border-cyan-700/50 cursor-pointer transition-colors"
              onClick={() => loadSession(s.id)}
              data-testid={`session-${s.id}`}
            >
              <CardContent className="py-3 px-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <MessageSquare className="w-5 h-5 text-cyan-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-white text-sm truncate">{s.titel || 'Konversation'}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-slate-500"><Clock className="w-3 h-3 inline mr-1" />{formatDate(s.updated_date || s.created_date)}</span>
                        <span className="text-xs text-slate-600">{s.nachrichten_count || 0} Nachrichten</span>
                        {s.kontext_typ && <Badge variant="outline" className="text-[10px] py-0">{s.kontext_typ}</Badge>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={e => deleteSession(s.id, e)} className="h-7 w-7 p-0 text-slate-500 hover:text-red-400" data-testid={`delete-session-${s.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                    <ChevronRight className="w-4 h-4 text-slate-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
